/**
 * Drop-in replacement for `React.lazy()` that recovers from stale-chunk
 * errors after deployment by triggering a single auto-reload, then rethrowing
 * so a wrapping ErrorBoundary can render a fallback.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import {
  RELOAD_FLAG,
  RELOAD_FLAG_PREFIX,
  isChunkErrorMessage,
} from "./chunkErrorConstants";

// Track per-route reload attempts using WeakMap to ensure unique keys
// even if the factory source string is minified or non-extractable.
const lazyRetryCache = new WeakMap<Function, string>();
let retryKeyCounter = 0;

/**
 * Get or create a unique sessionStorage key for a factory.
 * Uses WeakMap to guarantee uniqueness without depending on source parsing.
 */
function getStorageKey(factory: Function): string {
  if (!lazyRetryCache.has(factory)) {
    lazyRetryCache.set(factory, `${RELOAD_FLAG_PREFIX}${retryKeyCounter++}`);
  }
  return lazyRetryCache.get(factory)!;
}

/**
 * Drop-in replacement for {@link React.lazy} with automatic chunk-error recovery.
 *
 * **On first chunk-load failure (production only)**: sets a sessionStorage guard
 * flag and triggers a single hard `window.location.reload()`. The fresh page
 * load fetches the updated HTML and chunk manifest, resolving the stale-URL
 * problem transparently.
 *
 * **On second consecutive failure**: rethrows the original error so the nearest
 * `<ErrorBoundary>` can render a user-friendly fallback.
 *
 * **In development**: errors are rethrown immediately to preserve HMR and
 * stack traces without triggering reload loops.
 *
 * @param factory - A zero-argument dynamic import factory,
 *   e.g. `() => import('./pages/Auth')`
 * @returns A `LazyExoticComponent` wrapping the imported component
 * @throws The original chunk-load error if a recovery reload has already been
 *   attempted for this route in the current browser session
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    const storageKey = getStorageKey(factory);

    try {
      const loadedModule = await factory();
      // Successful load — clear the per-route reload flag so the next
      // deployment can trigger auto-recovery for this route.
      if (typeof window !== "undefined") {
        window.sessionStorage.removeItem(storageKey);
      }
      return loadedModule;
    } catch (error) {
      // In development, rethrow immediately to preserve HMR and stack traces.
      if (!import.meta.env.PROD) {
        throw error;
      }

      // Non-chunk errors are rethrown immediately so Sentry captures them.
      if (!isChunkErrorMessage(error instanceof Error ? error.message : "")) {
        throw error;
      }

      // Check if a reload was already attempted for this route or globally.
      // Either flag means we already tried recovery — avoid loops.
      const reloadAttempted =
        typeof window !== "undefined" &&
        (window.sessionStorage.getItem(storageKey) === "true" ||
          window.sessionStorage.getItem(RELOAD_FLAG) === "true");

      if (reloadAttempted) {
        // Recovery reload didn't fix it — surface to ErrorBoundary.
        // Note: sessionStorage flags already encode the state for debugging.
        if (import.meta.env.DEV) {
          console.error(
            `[lazyWithRetry] Chunk still failed after reload for route: ${storageKey}`,
          );
        }
        throw error;
      }

      // First failure — trigger recovery reload. Set both flags so other
      // recovery layers (ChunkErrorBoundary, chunkErrorHandler) see the flag
      // and skip their own reload attempts.
      if (import.meta.env.DEV) {
        console.warn(`[lazyWithRetry] Attempting chunk recovery reload for route: ${storageKey}`);
      }

      if (typeof window !== "undefined") {
        window.sessionStorage.setItem(storageKey, "true");
        window.sessionStorage.setItem(RELOAD_FLAG, "true");
        window.location.reload();
      }

      // Return a never-settling Promise to keep Suspense in place while
      // the page reloads, avoiding a brief error flash.
      return new Promise<never>(() => {});
    }
  });
}
